import { t } from "../i18n/index.js";
import { currentControls, keyLabel, moveKeys, primaryKey } from "../input/bindings.js";
import "./controlsStrip.css";

/**
 * Los controles, EN el partido: una tira discreta abajo a la izquierda.
 *
 * Pedido en Discord (2026-09-29): "estaría bien tener los controles en
 * pantalla durante el partido, sobre todo en la dificultad más fácil". Los
 * del menu se leen antes de jugar y se olvidan al primer balon. Sale en los
 * primeros partidos de cada navegador (`FIRST_MATCHES`) y siempre en el Barrio,
 * que es donde esta quien empieza. Con las teclas de CADA uno (CONTROLES), y
 * las del mando si hay uno de verdad conectado. No atrapa clics: es para leer.
 */
const FIRST_MATCHES = 5;
const COUNT_KEY = "fynta.matchesStarted";

/** Cuenta este partido y dice si toca enseñar la tira. */
export function wantsControlsStrip(barrio: boolean): boolean {
  let started = 0;
  try {
    started = Number(localStorage.getItem(COUNT_KEY) ?? "0") || 0;
    localStorage.setItem(COUNT_KEY, String(started + 1));
  } catch {
    // Sin almacenamiento, como si fuera el primero: mejor de mas que de menos.
  }
  return barrio || started < FIRST_MATCHES;
}

function hasPad(): boolean {
  const pads = typeof navigator.getGamepads === "function" ? navigator.getGamepads() : [];
  return [...pads].some((pad) => pad?.connected && pad.mapping === "standard");
}

function stripHtml(): string {
  const kbd = (label: string): string => `<kbd>${label.replace(/[&<>"]/g, "")}</kbd>`;
  if (hasPad()) {
    return `
      <span>${kbd(t("Stick"))} ${t("moverte")}</span>
      <span>${kbd("A")} ${t("golpear · mantén: tiro")}</span>
      <span>${kbd("X")} ${t("regate")}</span>`;
  }
  const mouse = currentControls().mouseAim
    ? `<span>${kbd(t("Clic"))} ${t("golpear hacia el ratón")}</span>`
    : "";
  return `
    <span>${moveKeys().map(kbd).join("")} ${t("moverte")}</span>
    <span>${kbd(primaryKey("kick"))} ${t("golpear · mantén: tiro")}</span>
    <span>${kbd(primaryKey("dash"))} ${t("regate")}</span>
    ${mouse}
    <span>${kbd(keyLabel("Tab"))} ${t("estadísticas")}</span>`;
}

export function createControlsStrip(parent: HTMLElement) {
  const root = document.createElement("div");
  root.className = "controls-strip";
  root.setAttribute("aria-hidden", "true");
  root.innerHTML = stripHtml();
  parent.appendChild(root);

  // Un mando que se conecta a mitad cambia lo que hay que enseñar.
  const refresh = (): void => {
    root.innerHTML = stripHtml();
  };
  window.addEventListener("gamepadconnected", refresh);
  window.addEventListener("gamepaddisconnected", refresh);

  return {
    dispose(): void {
      window.removeEventListener("gamepadconnected", refresh);
      window.removeEventListener("gamepaddisconnected", refresh);
      root.remove();
    },
  };
}

export type ControlsStrip = ReturnType<typeof createControlsStrip>;
